import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import type { ChatConversation, ConversationsResponse, CourseCard, LessonSource, MessagesResponse, UnreadCountResponse } from './chatbot.models';

@Injectable({
  providedIn: 'root'
})
export class ChatbotService {
  private readonly base = `${environment.apiBaseUrl}/chat`;
  private readonly streamInactivityTimeoutMs = 40000;

  /** Global unread badge count. Updated by socket events and API calls. */
  readonly unreadCount = signal(0);

  constructor(private http: HttpClient) {}

  private processSseEventBlock(
    eventLines: string[],
    onChunk: (chunk: string) => void,
    onDone: (
      messageId: string,
      suggestions: string[],
      courseUrl?: string,
      lessonSources?: LessonSource[],
      blockedSources?: LessonSource[],
      courseCards?: CourseCard[],
      noEvidence?: boolean
    ) => void,
    onError: (err: string) => void,
    hasReceivedContent: { value: boolean }
  ): boolean {
    const dataLines: string[] = [];

    for (const rawLine of eventLines) {
      const line = rawLine.replace(/\r$/, '');

      if (!line || line.startsWith(':')) {
        continue;
      }

      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^ /, ''));
        continue;
      }

      if (line.startsWith('event:') || line.startsWith('id:')) {
        continue;
      }
    }

    if (dataLines.length === 0) {
      return false;
    }

    const raw = dataLines.join('\n').trim();
    if (!raw) {
      return false;
    }

    try {
      const event = JSON.parse(raw);

      if (event.error) {
        onError(this.extractStreamError(undefined, event.message, 'Error del servidor'));
        return true;
      }

      const chunk = typeof event.chunk === 'string' ? event.chunk : '';

      if (event.done) {
        const doneContent = typeof event.content === 'string' ? event.content : chunk;

        if (doneContent.trim().length > 0) {
          hasReceivedContent.value = true;
          onChunk(doneContent);
        }

        if (!hasReceivedContent.value) {
          onError(this.extractStreamError(undefined, undefined, 'El asistente no devolvió respuesta. Inténtalo de nuevo.'));
          return true;
        }

        onDone(
          event.messageId,
          event.suggestions ?? [],
          event.courseUrl,
          event.lessonSources ?? [],
          event.blockedSources ?? [],
          event.courseCards ?? [],
          event.noEvidence ?? false
        );
        return true;
      }

      if (chunk.length > 0) {
        if (chunk.trim().length > 0) {
          hasReceivedContent.value = true;
        }
        onChunk(chunk);
      }
    } catch {
      // Ignore malformed event payloads
    }

    return false;
  }

  private processSseLines(
    lines: string[],
    pendingEventLines: string[],
    onChunk: (chunk: string) => void,
    onDone: (
      messageId: string,
      suggestions: string[],
      courseUrl?: string,
      lessonSources?: LessonSource[],
      blockedSources?: LessonSource[],
      courseCards?: CourseCard[],
      noEvidence?: boolean
    ) => void,
    onError: (err: string) => void,
    hasReceivedContent: { value: boolean }
  ): boolean {
    for (const rawLine of lines) {
      const line = rawLine.replace(/\r$/, '');

      if (line === '') {
        if (pendingEventLines.length === 0) {
          continue;
        }

        const shouldStop = this.processSseEventBlock(pendingEventLines, onChunk, onDone, onError, hasReceivedContent);
        pendingEventLines.length = 0;
        if (shouldStop) {
          return true;
        }
        continue;
      }

      pendingEventLines.push(line);
    }

    return false;
  }

  private extractStreamError(status: number | undefined, bodyMessage: string | undefined, fallback: string): string {
    if (status === 429) return 'El asistente está recibiendo muchas peticiones. Espera unos segundos e inténtalo de nuevo.';
    if (status === 401 || status === 403) return 'Tu sesión ha caducado. Recarga la página.';
    if (status && status >= 500) return 'El servicio está temporalmente no disponible. Inténtalo en unos minutos.';
    return bodyMessage || fallback;
  }

  // ----------------------------------------------------------------
  // Conversations
  // ----------------------------------------------------------------

  async listConversations(page = 1, limit = 20): Promise<ConversationsResponse> {
    return firstValueFrom(
      this.http.get<ConversationsResponse>(`${this.base}/conversations`, {
        params: { page, limit }
      })
    );
  }

  async createConversation(): Promise<ChatConversation> {
    const res = await firstValueFrom(this.http.post<{ success: boolean; data: ChatConversation }>(`${this.base}/conversations`, {}));
    return res.data;
  }

  async renameConversation(conversationId: string, title: string): Promise<void> {
    await firstValueFrom(this.http.patch(`${this.base}/conversations/${conversationId}/rename`, { title }));
  }

  async deleteConversation(conversationId: string): Promise<void> {
    await firstValueFrom(this.http.delete(`${this.base}/conversations/${conversationId}`));
  }

  async archiveConversation(conversationId: string): Promise<void> {
    await firstValueFrom(this.http.patch(`${this.base}/conversations/${conversationId}/archive`, {}));
  }

  // ----------------------------------------------------------------
  // Messages
  // ----------------------------------------------------------------

  async getMessages(conversationId: string, page = 1, limit = 50): Promise<MessagesResponse> {
    return firstValueFrom(
      this.http.get<MessagesResponse>(`${this.base}/conversations/${conversationId}/messages`, {
        params: { page, limit }
      })
    );
  }

  /**
   * Opens a Server-Sent Event stream for the assistant's response.
   * Calls onChunk for each token, then onDone when complete.
   */
  sendMessageStream(
    conversationId: string,
    message: string,
    courseId: string | undefined,
    onChunk: (chunk: string) => void,
    onDone: (
      messageId: string,
      suggestions: string[],
      courseUrl?: string,
      lessonSources?: LessonSource[],
      blockedSources?: LessonSource[],
      courseCards?: CourseCard[],
      noEvidence?: boolean
    ) => void,
    onError: (err: string) => void
  ): { abort: () => void; promise: Promise<void> } {
    const token = localStorage.getItem('et');
    const url = `${this.base}/conversations/${conversationId}/messages`;
    const abortController = new AbortController();
    let inactivityTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let settled = false;
    let silentAbortRequested = false;
    let watchdogTriggered = false;
    const pendingEventLines: string[] = [];
    const hasReceivedContent = { value: false };

    const clearInactivityTimeout = (): void => {
      if (inactivityTimeoutId) {
        clearTimeout(inactivityTimeoutId);
        inactivityTimeoutId = null;
      }
    };

    const markSettled = (): void => {
      settled = true;
      clearInactivityTimeout();
    };

    const resetInactivityTimeout = (): void => {
      clearInactivityTimeout();
      if (settled) return;
      inactivityTimeoutId = setTimeout(() => {
        if (settled) return;
        watchdogTriggered = true;
        clearInactivityTimeout();
        abortController.abort();
      }, this.streamInactivityTimeoutMs);
    };

    const failStream = (status: number | undefined, bodyMessage: string | undefined, fallback: string): void => {
      if (settled) return;
      markSettled();
      onError(this.extractStreamError(status, bodyMessage, fallback));
    };

    const abort = (): void => {
      if (settled || silentAbortRequested) return;
      silentAbortRequested = true;
      clearInactivityTimeout();
      abortController.abort();
    };

    // SSE via fetch + ReadableStream (EventSource doesn't support POST)
    resetInactivityTimeout();
    const promise = fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ message, ...(courseId ? { courseId } : {}) }),
      signal: abortController.signal
    })
      .then(async (response) => {
        resetInactivityTimeout();

        if (!response.ok) {
          let bodyMessage: string | undefined;
          try {
            const rawBody = await response.text();
            if (rawBody) {
              try {
                const body = JSON.parse(rawBody);
                bodyMessage = body?.message || body?.error?.message || body?.error;
              } catch {
                bodyMessage = rawBody.trim() || undefined;
              }
            }
          } catch {}
          failStream(response.status, bodyMessage, 'Error al enviar el mensaje');
          return;
        }

        if (!response.body) {
          failStream(response.status, undefined, 'La respuesta del asistente no se pudo procesar.');
          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          if (!value || value.byteLength === 0) {
            continue;
          }

          resetInactivityTimeout();

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          if (this.processSseLines(lines, pendingEventLines, onChunk, onDone, onError, hasReceivedContent)) {
            markSettled();
            return;
          }
        }

        buffer += decoder.decode();
        const trailingLines = buffer.length > 0 ? buffer.split('\n') : [];
        if (trailingLines.length > 0 && this.processSseLines(trailingLines, pendingEventLines, onChunk, onDone, onError, hasReceivedContent)) {
          markSettled();
          return;
        }

        if (pendingEventLines.length > 0 && this.processSseEventBlock(pendingEventLines, onChunk, onDone, onError, hasReceivedContent)) {
          pendingEventLines.length = 0;
          markSettled();
          return;
        }

        if (!settled) {
          failStream(response.status, undefined, 'La respuesta del asistente se interrumpió antes de completarse');
        }
      })
      .catch((err) => {
        if (settled) return;
        if (silentAbortRequested) {
          markSettled();
          return;
        }
        if (watchdogTriggered || err?.name === 'AbortError') {
          failStream(undefined, undefined, 'El asistente está tardando demasiado en responder. Prueba de nuevo en unos minutos.');
          return;
        }
        failStream(undefined, err?.message, 'Error de conexión al contactar con el asistente.');
      });

    return { abort, promise };
  }

  async submitFeedback(conversationId: string, messageId: string, feedback: 'positive' | 'negative'): Promise<void> {
    await firstValueFrom(this.http.patch(`${this.base}/conversations/${conversationId}/messages/${messageId}/feedback`, { feedback }));
  }

  async markAsRead(conversationId: string): Promise<void> {
    await firstValueFrom(this.http.patch(`${this.base}/conversations/${conversationId}/read`, {}));
  }

  // ----------------------------------------------------------------
  // Unread badge
  // ----------------------------------------------------------------

  async fetchUnreadCount(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.get<UnreadCountResponse>(`${this.base}/unread`));
      this.unreadCount.set(res.data.unreadCount);
    } catch {
      // Silently fail — badge will just stay at 0
    }
  }

  addUnread(): void {
    this.unreadCount.update((c) => c + 1);
  }

  clearUnread(): void {
    this.unreadCount.set(0);
  }

  // ----------------------------------------------------------------
  // Status (enabled + user allowlist check)
  // ----------------------------------------------------------------

  async fetchStatus(): Promise<{ enabled: boolean; userAllowed: boolean }> {
    try {
      const res = await firstValueFrom(this.http.get<{ success: boolean; data: { enabled: boolean; userAllowed: boolean } }>(`${this.base}/status`));
      return res.data;
    } catch {
      // Fail open — the backend enforces access; this is a UX hint only
      return { enabled: true, userAllowed: true };
    }
  }
}
